import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';

interface IProfile {
  login: string;
  display: string;
  bio: string;
  location: string;
  site: string;
  joined: string;
  contributions: number;
  orgs: string[];
  repos: string[];
}

/** The single mocked account that the (mock) auth flow signs in as. */
const KNOWN_USERS: Record<string, IProfile> = {
  'maya-k': {
    login: 'maya-k',
    display: 'Maya Kettler',
    bio: 'Systems & web infra. Maintainer of dcrouter and dees-catalog. Likes tries, benchmarks, and small bundles.',
    location: 'Bregenz, AT',
    site: 'maya.codes',
    joined: 'Joined 2018',
    contributions: 1284,
    orgs: ['serve.zone', 'design.estate', 'git.zone'],
    repos: ['serve.zone/dcrouter', 'design.estate/dees-catalog'],
  },
};

/**
 * Public user profile (mock). Mirrors the Claude Design `social.jsx` UserView: avatar +
 * identity column with bio/links/orgs, a pinned-repos grid, and a contribution grid.
 * Own-profile shows "Edit profile" (→ settings); profile-form edits made in settings are
 * read back from `fg.profileEdits`. Other logins fall back to a minimal placeholder.
 */
@customElement('dotglobal-user-profile')
export class DotglobalUserProfile extends DeesElement {
  @property({ type: String })
  public accessor login = '';

  @property({ type: Boolean })
  public accessor isOwnProfile = false;

  @state()
  private accessor following = false;

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; }
      .profile-layout { display: flex; gap: 24px; align-items: flex-start; }
      .profile-side { width: 280px; flex-shrink: 0; }
      .uname { font-size: 22px; font-weight: 700; font-family: var(--font-display); letter-spacing: -0.01em; }
      .repocard {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 16px;
        border: 1px solid var(--border-default);
        border-radius: 8px;
        background: var(--bg-primary);
        cursor: pointer;
        transition: border-color 0.15s, background 0.15s;
      }
      .repocard:hover { border-color: var(--border-strong); background: var(--bg-secondary); }
      .rcname { display: flex; align-items: center; gap: 8px; color: var(--accent); }
      .rcsub { font-size: 12.5px; color: var(--text-secondary); }
      .pinned-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 14px; }
      .orgrow { display: flex; gap: 8px; flex-wrap: wrap; }
      .orgrow .avatar { cursor: pointer; }
      .contrib-grid { display: grid; grid-template-columns: repeat(30, 1fr); gap: 3px; }
      .contrib-grid span { aspect-ratio: 1; border-radius: 2px; }
      .uplabel { font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-muted); }
      @media (max-width: 820px) {
        .profile-layout { flex-direction: column; }
        .profile-side { width: 100%; }
      }
    `,
  ];

  public render(): TemplateResult {
    const profile = this.resolveProfile();
    return html`
      <div class="page content">
        <div class="profile-layout">
          <div class="stack gap14 profile-side">
            ${this.renderAvatar(profile.login, 150)}
            <div>
              <div class="uname">${profile.display}</div>
              <div class="muted mono" style="font-size: 14px">${profile.login}</div>
            </div>
            ${profile.bio ? html`<div class="sec" style="font-size: 13.5px; line-height: 1.55">${profile.bio}</div>` : ''}
            ${this.isOwnProfile
              ? html`<button class="btn" @click=${() => this.navigate('#/settings')}>${this.icon('Pencil')} Edit profile</button>`
              : html`<button class=${`btn ${this.following ? '' : 'primary'}`} @click=${() => this.toggleFollow(profile)}>${this.icon(this.following ? 'Check' : 'Plus')} ${this.following ? 'Following' : 'Follow'}</button>`}
            <div class="stack gap8" style="font-size: 13px">
              ${profile.location ? html`<div class="row gap8 muted">${this.icon('Globe')} ${profile.location}</div>` : ''}
              ${profile.site ? html`<a class="row gap8" style="color: var(--accent)" href=${this.siteHref(profile.site)} target="_blank" rel="noopener">${this.icon('Link')} ${profile.site}</a>` : ''}
              ${profile.joined ? html`<div class="row gap8 muted">${this.icon('Clock')} ${profile.joined}</div>` : ''}
            </div>
            ${profile.orgs.length
              ? html`
                  <div class="stack gap8">
                    <div class="uplabel">Organizations</div>
                    <div class="orgrow">
                      ${profile.orgs.map((orgArg) => html`<span @click=${() => this.navigate(`#/org/${orgArg}`)}>${this.renderAvatar(orgArg, 34)}</span>`)}
                    </div>
                  </div>
                `
              : ''}
          </div>

          <div class="grow stack gap16" style="min-width: 0">
            <div class="row gap10" style="justify-content: space-between">
              <div class="sectitle" style="font-size: 17px">Pinned</div>
              ${profile.contributions
                ? html`<div class="muted" style="font-size: 13px"><b class="mono" style="color: var(--text-primary)">${profile.contributions.toLocaleString()}</b> contributions this year</div>`
                : ''}
            </div>
            ${profile.repos.length
              ? html`
                  <div class="pinned-grid">
                    ${profile.repos.map((slugArg) => this.renderRepoCard(slugArg))}
                  </div>
                `
              : html`<div class="muted" style="font-size: 13px">No pinned repositories.</div>`}
            <div class="card" style="padding: 16px">
              <div class="row gap8" style="font-weight: 600; font-size: 13px; margin-bottom: 12px">${this.icon('Zap')} Contribution activity</div>
              <div class="contrib-grid">
                ${Array.from({ length: 150 }).map((_unusedArg, indexArg) => {
                  const level = [0, 0, 1, 0, 2, 1, 3, 0, 1, 4, 2][Math.floor(Math.abs(Math.sin(indexArg * 1.7)) * 11)];
                  const background = level === 0 ? 'var(--bg-tertiary)' : `color-mix(in srgb, var(--accent) ${level * 25}%, var(--bg-tertiary))`;
                  return html`<span style=${`background:${background}`}></span>`;
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderRepoCard(slugArg: string): TemplateResult {
    const [org, name] = slugArg.split('/');
    return html`
      <div class="repocard" @click=${() => this.navigate(`#/repo/${org}/${name}`)}>
        <div class="rcname">${this.icon('BookOpen')} <span style="font-size: 13px"><span class="muted">${org}/</span><b>${name}</b></span></div>
        <div class="rcsub">View repository on foss.global →</div>
      </div>
    `;
  }

  private resolveProfile(): IProfile {
    const base = KNOWN_USERS[this.login];
    if (!base) {
      return {
        login: this.login || 'unknown',
        display: this.login || 'unknown',
        bio: '',
        location: '',
        site: '',
        joined: '',
        contributions: 0,
        orgs: [],
        repos: [],
      };
    }
    if (this.login !== 'maya-k') {
      return base;
    }
    const edits = this.readProfileEdits();
    return {
      ...base,
      display: edits.display || base.display,
      bio: edits.bio || base.bio,
      site: edits.site || base.site,
      location: edits.location || base.location,
    };
  }

  private readProfileEdits(): { display?: string; bio?: string; site?: string; location?: string } {
    try {
      return JSON.parse(globalThis.localStorage?.getItem('fg.profileEdits') ?? '{}') || {};
    } catch {
      return {};
    }
  }

  private toggleFollow(profileArg: IProfile): void {
    this.following = !this.following;
    this.dispatchEvent(new CustomEvent('dotglobal-toast', {
      detail: { message: this.following ? `Following ${profileArg.login}` : 'Unfollowed' },
      bubbles: true,
      composed: true,
    }));
  }

  private siteHref(siteArg: string): string {
    return /^https?:\/\//.test(siteArg) ? siteArg : `https://${siteArg}`;
  }

  private navigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${14}></dees-icon>`;
  }

  private renderAvatar(labelArg: string, sizeArg: number): TemplateResult {
    const initials = labelArg.split(/[.\s/-]+/).filter(Boolean).slice(0, 2).map((partArg) => partArg[0]?.toUpperCase()).join('') || 'fg';
    return html`<span class="avatar" style=${`width:${sizeArg}px; height:${sizeArg}px; font-size:${Math.round(sizeArg * 0.36)}px`}>${initials}</span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-user-profile': DotglobalUserProfile;
  }
}
